import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentMultiGetStartedData,
  type EnrollmentMultiGetStartedSubmitInput,
  type EnrollmentMultiGetStartedSubmitOutput,
  type EnrollmentMultiGetStartedDataInput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'
import { getEnrollmentMultiGetStartedDataQuery } from './queries'

// Re-export query for backward compatibility
export { getEnrollmentMultiGetStartedDataQuery }

/**
 * Get Enrollment Multi GetStarted data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiGetStartedData>
 * @example const response = await getEnrollmentMultiGetStartedData()
 */
export const getEnrollmentMultiGetStartedData = async (
  input?: EnrollmentMultiGetStartedDataInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiGetStartedData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiGetStartedData',
      query: getEnrollmentMultiGetStartedDataQuery,
    },
    {
      submitEnrollmentInput: input,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiGetStartedData }
  return response.submitEnrollment
}

export const useGetEnrollmentMultiGetStartedData = createQueryHook<
  EnrollmentMultiGetStartedDataInput,
  EnrollmentMultiGetStartedData
>(getEnrollmentMultiGetStartedData)

/**
 * Submit Enrollment Multi GetStarted data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiGetStartedSubmitOutput>
 * @example const response = await submitEnrollmentMultiGetStartedData();
 */
export const submitEnrollmentMultiGetStartedData = async (
  enrollmentGetStartedSubmitInput: EnrollmentMultiGetStartedSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiGetStartedSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiGetStartedData',
      query: `mutation submitEnrollmentMultiGetStartedData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentGetStartedSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiGetStartedSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiGetStartedData = createMutationHook<
  EnrollmentMultiGetStartedSubmitInput,
  EnrollmentMultiGetStartedSubmitOutput
>(submitEnrollmentMultiGetStartedData)
